import { FormikField } from "@components/forms/FormikField/FormikField";
import { Picker } from "@design-system/Picker/Picker.lib";
import { getMultiSelectTriggerValue } from "../lib";
import fieldSpacing from "./fieldSpacing.module.scss";

interface MultiSelectPickerFieldItem<T extends string> {
    value: T;
    label: string;
}

interface MultiSelectPickerFieldProps<T extends string> {
    name: string;
    label: string;
    items: ReadonlyArray<MultiSelectPickerFieldItem<T>>;
    searchable?: boolean;
    emptyLabel?: string;
    showErrorMessage?: boolean;
    triggerClassName?: string;
}

export const MultiSelectPickerField = <T extends string>({
    name,
    label,
    items,
    searchable,
    emptyLabel,
    showErrorMessage,
    triggerClassName,
}: MultiSelectPickerFieldProps<T>) => {
    const labelByValue = new Map(items.map((item) => [item.value, item.label]));

    return (
        <FormikField<T[]>
            name={name}
            label={label}
            control="button"
            showErrorMessage={showErrorMessage}
            className={fieldSpacing.buttonField}
        >
            {({ helpers, field, invalid }) => (
                <Picker.Select
                    multiple
                    searchable={searchable}
                    searchPlaceholder={searchable ? "Search…" : undefined}
                    emptyLabel={emptyLabel}
                    items={items}
                    value={field.value ?? []}
                    onOpenChange={(open) => {
                        if (!open) {
                            helpers.setTouched(true);
                        }
                    }}
                    onValueChange={(value) => {
                        helpers.setValue(value);
                    }}
                    renderValue={(selectedValues) =>
                        getMultiSelectTriggerValue(
                            [...selectedValues],
                            items.length,
                            (value) => labelByValue.get(value) ?? value,
                        )
                    }
                    placeholder="Select"
                    aria-invalid={invalid}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="xl"
                            block
                            className={triggerClassName}
                        />
                    }
                />
            )}
        </FormikField>
    );
};
